/** Client-side mirror of server intune compliance logic (dashboard + asset filtering). */

export const INTUNE_COMPLIANCE_TYPES = {
  conditionalAccess: 'Device compliance policies',
  encryption: 'Encryption',
  antiMalware: 'Anti-Malware'
};

const INCLUDED_CHASSIS = new Set(['computer', 'mobile']);

function getObjectProperty(obj, key) {
  if (!obj || typeof obj !== 'object') return undefined;
  if (Object.prototype.hasOwnProperty.call(obj, key)) return obj[key];
  const lower = key.toLowerCase();
  const match = Object.keys(obj).find((k) => k.toLowerCase() === lower);
  return match != null ? obj[match] : undefined;
}

function getIntuneRawValue(intuneData, pathSegments) {
  if (!intuneData || typeof intuneData !== 'object') return undefined;
  let current = intuneData;
  for (const segment of pathSegments) {
    current = getObjectProperty(current, segment);
    if (current === undefined) return undefined;
  }
  return current;
}

function pickIntuneRawValue(intuneData, candidatePaths) {
  for (const path of candidatePaths) {
    const value = getIntuneRawValue(intuneData, path);
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return undefined;
}

function isTruthyJsonBool(value) {
  if (value == null) return false;
  return value === true || value === 1 || String(value).toLowerCase() === 'true';
}

export function parseIntuneRawColumn(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function getAssetChassisType(asset) {
  return asset?.ChassisType ?? asset?.chassistype ?? asset?.chassisType ?? asset?.type ?? null;
}

export function getAssetImei(asset) {
  const intuneData = parseIntuneRawColumn(asset?.['intune-raw']);
  const imei = pickIntuneRawValue(intuneData, [
    ['AdditionalProperties', 'Imei'],
    ['AdditionalProperties', 'IMEI'],
    ['Imei'],
    ['IMEI']
  ]);
  if (imei == null || imei === '') return null;
  return String(imei);
}

export function isExcludedIntuneComplianceChassisType(chassisType) {
  if (chassisType == null || String(chassisType).trim() === '') return true;
  return !INCLUDED_CHASSIS.has(String(chassisType).trim().toLowerCase());
}

export function isConditionalAccessCompliant(intuneData) {
  if (!intuneData) return false;
  const state = pickIntuneRawValue(intuneData, [
    ['AdditionalProperties', 'ComplianceState'],
    ['Status'],
    ['ComplianceState']
  ]);
  if (state == null || state === '') return false;
  return String(state).toLowerCase() === 'compliant';
}

export function isEncryptionCompliant(intuneData) {
  if (!intuneData) return false;
  const encrypted = pickIntuneRawValue(intuneData, [
    ['AdditionalProperties', 'IsEncrypted'],
    ['IsEncrypted']
  ]);
  return isTruthyJsonBool(encrypted);
}

export function isAntiMalwareCompliant(intuneData) {
  if (!intuneData) return false;
  const enabled = pickIntuneRawValue(intuneData, [
    ['WindowsProtectionState', 'malwareProtectionEnabled'],
    ['AdditionalProperties', 'malwareProtectionEnabled'],
    ['malwareProtectionEnabled']
  ]);
  return isTruthyJsonBool(enabled);
}

export function isAssetIntuneComplianceCompliant(asset, complianceType) {
  const intuneData = parseIntuneRawColumn(asset['intune-raw']);
  switch (complianceType) {
    case 'conditionalAccess':
      return isConditionalAccessCompliant(intuneData);
    case 'encryption':
      return isEncryptionCompliant(intuneData);
    case 'antiMalware':
      return isAntiMalwareCompliant(intuneData);
    default:
      return true;
  }
}

export function isAssetActiveForIntuneCompliance(asset) {
  const active = asset?.isActive ?? asset?.isactive;
  return active === 1 || active === true;
}

/** Match dashboard scope: active devices excluding certain chassis types. */
export function isAssetInIntuneComplianceScope(asset) {
  if (!isAssetActiveForIntuneCompliance(asset)) return false;
  return !isExcludedIntuneComplianceChassisType(getAssetChassisType(asset));
}

export function assetMatchesIntuneComplianceFilter(asset, complianceType, status = 'nonCompliant') {
  if (!complianceType || !INTUNE_COMPLIANCE_TYPES[complianceType]) return true;
  if (!isAssetInIntuneComplianceScope(asset)) return false;
  const compliant = isAssetIntuneComplianceCompliant(asset, complianceType);
  return status === 'nonCompliant' ? !compliant : compliant;
}

export function getIntuneComplianceFilterLabel(complianceType) {
  const name = INTUNE_COMPLIANCE_TYPES[complianceType] || complianceType;
  return `Not compliant: ${name}`;
}

const DEVICE_COMPLIANCE_POLICY_PATHS = [
  ['deviceCompliancePolicies'],
  ['deviceCompliancePolicyStates'],
  ['AdditionalProperties', 'deviceCompliancePolicies'],
  ['AdditionalProperties', 'deviceCompliancePolicyStates']
];

function normalizeIntunePolicyCollection(value) {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value.filter((item) => item != null && typeof item === 'object');
  }
  if (typeof value === 'object') {
    const displayName = getObjectProperty(value, 'displayName') ?? getObjectProperty(value, 'name');
    const state = getObjectProperty(value, 'state');
    if (displayName != null || state != null) {
      return [value];
    }
    return Object.keys(value)
      .sort((a, b) => {
        const numA = Number(a);
        const numB = Number(b);
        if (Number.isFinite(numA) && Number.isFinite(numB)) return numA - numB;
        return String(a).localeCompare(String(b));
      })
      .map((key) => value[key])
      .filter((item) => item != null && typeof item === 'object');
  }
  return [];
}

function getPolicyDisplayName(item) {
  const displayName = getObjectProperty(item, 'displayName') ?? getObjectProperty(item, 'name');
  if (displayName == null || String(displayName).trim() === '') return null;
  return String(displayName).trim();
}

function getDeviceCompliancePolicyItems(intuneData) {
  if (!intuneData) return [];

  const seen = new Map();
  for (const path of DEVICE_COMPLIANCE_POLICY_PATHS) {
    const raw = getIntuneRawValue(intuneData, path);
    const items = normalizeIntunePolicyCollection(raw);
    for (const item of items) {
      const displayName = getPolicyDisplayName(item);
      if (!displayName) continue;

      const state = getObjectProperty(item, 'state');
      const normalizedState = state != null && String(state).trim() !== ''
        ? String(state).trim()
        : null;
      const key = displayName.toLowerCase();
      const existing = seen.get(key);

      if (!existing) {
        seen.set(key, {
          displayName,
          state: normalizedState || 'Unknown'
        });
        continue;
      }

      if (existing.state === 'Unknown' && normalizedState) {
        seen.set(key, { displayName, state: normalizedState });
      }
    }
  }

  return Array.from(seen.values());
}

export function getDeviceCompliancePolicyStates(asset) {
  const intuneData = parseIntuneRawColumn(asset?.['intune-raw']);
  return getDeviceCompliancePolicyItems(intuneData);
}

export function getDeviceCompliancePolicyNames(intuneData) {
  return getDeviceCompliancePolicyItems(intuneData).map((policy) => policy.displayName);
}

export function formatDeviceCompliancePolicyTooltip(policies) {
  if (!policies?.length) return '';
  return policies.map((policy) => `${policy.displayName}: ${policy.state}`).join('\n');
}
